"use client";

import { useId } from "react";
import { PHONE_FORMAT } from "@shared/formats/phone";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type {
  GuestContactState,
  UpdateQuoteFormState,
} from "./listing-quote-request-state";
import type { QuoteRequirementErrors } from "./listing-quote-requirements";

type ContactRequirement = "name" | "email" | "phone";

interface GuestContactFieldsProps {
  contact: GuestContactState;
  updateState: UpdateQuoteFormState;
  onEdit: () => void;
  onLeave: (field: ContactRequirement) => void;
  errors: QuoteRequirementErrors;
  includeEmail: boolean;
}

export function ListingGuestContactFields({
  contact,
  updateState,
  onEdit,
  onLeave,
  errors,
  includeEmail,
}: GuestContactFieldsProps) {
  const idPrefix = useId();
  const setContact = (patch: Partial<GuestContactState>) => {
    onEdit();
    updateState({ contact: { ...contact, ...patch } });
  };
  return (
    <fieldset className="mt-4 space-y-3">
      <legend className="mb-3 text-base font-semibold text-foreground">
        {includeEmail ? "Where should we send the quote?" : "Your contact details"}
      </legend>
      <ContactField
        id={`${idPrefix}-name`}
        label="Name"
        autoComplete="name"
        value={contact.name}
        requirement="name"
        error={errors.name}
        onChange={(name) => setContact({ name })}
        onBlur={() => onLeave("name")}
      />
      {includeEmail ? (
        <ContactEmailField
          id={`${idPrefix}-email`}
          email={contact.email}
          error={errors.email}
          onChange={(email) => setContact({ email })}
          onBlur={() => onLeave("email")}
        />
      ) : null}
      <ContactPhoneField
        id={`${idPrefix}-phone`}
        phone={contact.phone}
        error={errors.phone}
        onChange={(phone) => setContact({ phone })}
        onBlur={() => onLeave("phone")}
      />
    </fieldset>
  );
}

function ContactEmailField(props: {
  id: string;
  email: string;
  error: string | undefined;
  onChange: (email: string) => void;
  onBlur: () => void;
}) {
  return (
    <ContactField
      id={props.id}
      label="Email"
      type="email"
      autoComplete="email"
      value={props.email}
      requirement="email"
      error={props.error}
      onChange={props.onChange}
      onBlur={props.onBlur}
    />
  );
}

function ContactPhoneField(props: {
  id: string;
  phone: string;
  error: string | undefined;
  onChange: (phone: string) => void;
  onBlur: () => void;
}) {
  return (
    <ContactField
      id={props.id}
      label="Phone"
      type="tel"
      autoComplete="tel"
      placeholder={PHONE_FORMAT.display}
      value={props.phone}
      requirement="phone"
      error={props.error}
      onChange={(phone) => props.onChange(PHONE_FORMAT.format(phone))}
      onBlur={props.onBlur}
    />
  );
}

interface ContactFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  type?: "text" | "email" | "tel";
  autoComplete: string;
  placeholder?: string;
  requirement: ContactRequirement;
  error: string | undefined;
}

function ContactField({
  id,
  label,
  value,
  onChange,
  onBlur,
  type = "text",
  autoComplete,
  placeholder,
  requirement,
  error,
}: ContactFieldProps) {
  const errorId = `${id}-error`;
  return (
    <div className="space-y-1" data-quote-requirement={requirement}>
      <Label htmlFor={id} size="sm">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
      />
      {error ? (
        <p id={errorId} className="text-xs text-destructive-strong">
          {error}
        </p>
      ) : null}
    </div>
  );
}
